import Seo from "../components/Seo";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import { Input } from "../components/ui/input";
import { Search } from "lucide-react";
import { ARTICLE_CATEGORIES, categoryLabel } from "../lib/articleCategories";
import NetworkBackground from "../components/NetworkBackground";
import TierBadge from "../components/TierBadge";
import { FilterButton, FilterGroup, FilterSheet } from "../components/FilterSheet";

const CATEGORIES = [{ id: "all", label: "Tous" }, ...ARTICLE_CATEGORIES];
const ACCESS = [{ id: "all", label: "Tout" }, { id: "free", label: "Gratuits" }, { id: "premium", label: "Premium" }];

export default function ArticlesPage() {
  const [category, setCategory] = useState("all");
  const [access, setAccess] = useState("all");
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState(false); // téléphone : panneau des filtres

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["articles", category],
    queryFn: async () => {
      const params = category !== "all" ? { category } : {};
      const r = await api.get("/articles", { params });
      return r.data;
    },
  });

  const filtered = articles.filter((a) => {
    const tier = a.tier_required || "free";
    if (access === "free" && tier !== "free") return false;
    if (access === "premium" && tier === "free") return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      a.title.toLowerCase().includes(s) ||
      (a.excerpt || "").toLowerCase().includes(s) ||
      (a.tags || []).some((t) => t.toLowerCase().includes(s))
    );
  });
  // Article à la une : le plus récent de la liste (l'API renvoie les articles du plus récent au plus ancien), sauf pendant une recherche
  const featured = !q.trim() && filtered.length > 1 ? filtered[0] : null;
  const rest = featured ? filtered.slice(1) : filtered;

  return (
    <div className="min-h-screen">
      <Seo title="Analyses" description="Toutes les analyses de marché de Side B : décryptages, actualités et stratégies pour investir avec méthode." path="/articles" />
      <section className="relative border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-40" />
        <NetworkBackground />
        <div className="relative mx-auto max-w-7xl px-6 py-8 sm:py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-violet-400 mb-2">Bibliothèque</div>
          <h1 className="font-display font-black text-[2rem] leading-tight sm:text-5xl">Analyses</h1>
          <p className="mt-2 sm:mt-3 text-[15px] sm:text-base text-zinc-400 max-w-2xl">
            Nos lectures des marchés : le contexte, les chiffres et notre méthode. Les analyses premium sont incluses dès l'offre Essentiel.
          </p>

          {/* Ordinateur : recherche et filtres dans l'en-tête (téléphone : sous l'en-tête, voir plus bas) */}
          <div className="hidden sm:flex mt-8 flex-col sm:flex-row gap-4 sm:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="articles-search"
                placeholder="Rechercher une analyse, un mot-clé…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 bg-white/[0.02] border-white/10"
              />
            </div>
            <div className="inline-flex items-center gap-1 border border-white/10 rounded-lg p-1 bg-white/[0.02] self-start sm:self-auto" data-testid="articles-access">
              {ACCESS.map((o) => (
                <button
                  key={o.id}
                  onClick={() => setAccess(o.id)}
                  data-testid={`articles-access-${o.id}`}
                  className={`rounded-md px-3 py-1 text-xs uppercase tracking-[0.15em] transition-colors ${
                    access === o.id ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div className="hidden sm:flex mt-4 flex-wrap gap-2" data-testid="articles-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                data-testid={`articles-category-${c.id}`}
                className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.15em] border transition-colors ${
                  category === c.id
                    ? "bg-emerald-500 text-black border-emerald-500"
                    : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Téléphone : recherche + bouton « Filtres » sur une seule ligne, qui reste visible en haut pendant le défilement */}
      <div className="sm:hidden sticky top-14 z-30 border-b border-white/5 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="px-6 py-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Rechercher une analyse…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-11 pl-9 bg-white/[0.03] border-white/10"
              data-testid="articles-search-mobile"
            />
          </div>
          <FilterButton
            accent="violet"
            count={(category !== "all" ? 1 : 0) + (access !== "all" ? 1 : 0)}
            onClick={() => setSheet(true)}
            testid="articles-filter-button"
          />
        </div>
      </div>
      <FilterSheet
        open={sheet}
        onClose={() => setSheet(false)}
        onReset={() => { setCategory("all"); setAccess("all"); }}
        accent="violet"
        resultLabel={`Voir ${filtered.length} analyse${filtered.length > 1 ? "s" : ""}`}
      >
        <FilterGroup label="Accès" options={ACCESS} value={access} onChange={setAccess} accent="violet" />
        <FilterGroup label="Catégorie" options={CATEGORIES} value={category} onChange={setCategory} accent="violet" />
      </FilterSheet>

      <section className="mx-auto max-w-7xl px-6 pt-6 pb-10 sm:py-12">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-72 rounded-xl bg-white/[0.02] border border-white/5 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-zinc-500">
            Aucune analyse ne correspond à cette recherche.
          </div>
        ) : (
          <>
            {featured && (
              <Link to={`/articles/${featured.slug}`} className="block group mb-5" data-testid="articles-featured">
                <div className="rounded-xl border shadow overflow-hidden bg-card border-white/5 hover:border-emerald-500/30 transition-all grid md:grid-cols-[3fr_2fr]">
                  <div
                    className="aspect-[16/9] md:aspect-auto md:min-h-[300px] bg-gradient-to-br from-emerald-500/10 to-transparent border-b md:border-b-0 md:border-r border-white/5 relative overflow-hidden"
                    style={featured.cover_image ? { backgroundImage: `url(${featured.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                  >
                    {featured.tier_required && featured.tier_required !== "free" && (
                      <div className="absolute top-3 right-3">
                        <TierBadge tier="essentiel" locked={!!featured.is_locked} blur />
                      </div>
                    )}
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-violet-400 mb-3">À la une · {categoryLabel(featured.category)}</div>
                    <h2 className="font-display font-black text-2xl md:text-3xl leading-tight group-hover:text-emerald-300 transition-colors">{featured.title}</h2>
                    {featured.excerpt && <p className="mt-3 text-zinc-400 line-clamp-4">{featured.excerpt}</p>}
                    <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500">
                      <span>{featured.author}</span>
                      <span>{featured.created_at ? new Date(featured.created_at).toLocaleDateString("fr-FR") : ""}</span>
                    </div>
                  </div>
                </div>
              </Link>
            )}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="articles-grid">
                {rest.map((a) => (<ArticleCard key={a.id} article={a} />))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
